/**
 * cross-rotation – Instagram Exhibition Announcement Videos
 *
 * Generates 4 short videos (~10 s each, ≥5 s still frame) for announcing
 * exhibitions taking place across 10 Kunstkästen around a city.
 *
 * Video 1 (Aktuell)  – Fades in the cross. Shows exhibition title, artist,
 *                       datespan.  Holds still frame.
 * Video 2 (Ausblick) – Starts at end state of video 1, rotates to Ausblick.
 *                       Shows vernissage date, time, address.  Still frame.
 * Video 3 (Info)     – Starts at end state of video 2, rotates to Info.
 *                       Exhibition theme / description.  Still frame.
 * Video 4 (Archiv)   – Starts at end state of video 3, rotates to Archiv.
 *                       Artist details / bio.  Still frame.
 *
 * A "Video Step" dropdown selects which of the four videos to render.
 */

import React, { useEffect, useRef, useState } from "react";
import { animate } from "animejs";
import { createTimeline } from "animejs/timeline";
import { COMPONENT_ORDER } from "./useCrossRotation";
import "./styles.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const FPS = 60;
const STILL_FRAME_MS = 5000;

// Panels that always carry a scale(-1,-1) flip in addition to rotation
const FLIPPED_PANELS = new Set(["ausblick", "info"]);

// Base CSS rotations from styles.css (0 steps of rotation applied)
const BASE_ROTATIONS = { aktuell: 0, ausblick: 90, info: 0, archiv: 90 };

// Index of each named position in COMPONENT_ORDER
const POSITION_INDEX = { aktuell: 0, ausblick: 1, info: 2, archiv: 3 };

/** Return the absolute rotation for each panel when `steps` 90° rotations
 *  have been applied from the initial state. */
function rotationsAtStep(steps) {
  return Object.fromEntries(
    COMPONENT_ORDER.map((k) => [k, BASE_ROTATIONS[k] + steps * 90]),
  );
}

// ─── Map video step names → rotation targets ────────────────────────────────

const VIDEO_CONFIG = {
  1: { startAt: null, animateTo: null, panelKey: "aktuell" },
  2: { startAt: "aktuell", animateTo: "ausblick", panelKey: "ausblick" },
  3: { startAt: "ausblick", animateTo: "info", panelKey: "info" },
  4: { startAt: "info", animateTo: "archiv", panelKey: "archiv" },
};

const VIDEO_STEPS = Object.keys(VIDEO_CONFIG);

// ─── Handler ──────────────────────────────────────────────────────────────────

export const handler = ({ inputs, mechanic }) => {
  const {
    width,
    height,
    backgroundColor,
    textColor,
    inactiveBlur,
    inactiveOpacity,
    videoStep,
    // Step name labels
    label1,
    label2,
    label3,
    label4,
    // Video 1 – Aktuell
    exhibitionTitle,
    artistName,
    dateSpan,
    // Video 2 – Ausblick
    vernissageDate,
    vernissageTime,
    vernissageAddress,
    // Video 3 – Info
    exhibitionTheme,
    // Video 4 – Archiv
    artistDetails,
  } = inputs;

  const panelLabels = {
    aktuell: label1,
    ausblick: label2,
    info: label3,
    archiv: label4,
  };

  const config = VIDEO_CONFIG[videoStep];

  // ── Refs ───────────────────────────────────────────────────────────────
  const panelRefs = useRef({});
  const setPanelRef = (key) => (el) => {
    panelRefs.current[key] = el;
  };

  const totalFrames = useRef(0);
  const tlRef = useRef(null);
  const isPlayingRef = useRef(false);
  const rafRef = useRef(null);
  const [frameCount, setFrameCount] = useState(0);

  const startDrawLoop = () => {
    isPlayingRef.current = true;
    const loop = () => {
      if (!isPlayingRef.current) return;
      setFrameCount((c) => c + 1);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  const stopDrawLoop = () => {
    isPlayingRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  // ── Timeline setup (runs once, after first render) ─────────────────────
  useEffect(() => {
    const CONTENT_REVEAL_MS = 800;
    const BLUR_INACTIVE = `blur(${inactiveBlur}px)`;
    const OPACITY_INACTIVE = inactiveOpacity;

    // Disable CSS transitions on panels and their children so they don't
    // fight anime.js seeks
    COMPONENT_ORDER.forEach((key) => {
      const el = panelRefs.current[key];
      if (!el) return;
      el.style.transition = "none";
      const stepname = el.querySelector(".cross-stepname");
      const body = el.querySelector(".cross-body");
      if (stepname) stepname.style.transition = "none";
      if (body) body.style.transition = "none";
    });

    if (!config.animateTo) {
      // ── Video 1: panels fade in (blurred) → aktuell unblurs → still ──────
      // Timeline: 1150ms stagger + 800ms unblur + 5000ms still = 6950ms
      const TOTAL_MS = 1150 + CONTENT_REVEAL_MS + STILL_FRAME_MS;
      totalFrames.current = Math.ceil((TOTAL_MS / 1000) * FPS);

      const tl = createTimeline({ autoplay: false });

      // All panels fade in blurred — all content is visible from the start
      tl.add(
        ".cross-aktuell",
        {
          opacity: [0, OPACITY_INACTIVE],
          filter: BLUR_INACTIVE,
          duration: 400,
          ease: "outQuad",
        },
        0,
      );
      tl.add(
        ".cross-ausblick",
        {
          opacity: [0, OPACITY_INACTIVE],
          filter: BLUR_INACTIVE,
          duration: 400,
          ease: "outQuad",
        },
        250,
      );
      tl.add(
        ".cross-info",
        {
          opacity: [0, OPACITY_INACTIVE],
          filter: BLUR_INACTIVE,
          duration: 400,
          ease: "outQuad",
        },
        500,
      );
      tl.add(
        ".cross-archiv",
        {
          opacity: [0, OPACITY_INACTIVE],
          filter: BLUR_INACTIVE,
          duration: 400,
          ease: "outQuad",
        },
        750,
      );

      // Aktuell unblurs once all panels are visible; others stay blurred
      tl.add(
        ".cross-aktuell",
        {
          opacity: [OPACITY_INACTIVE, 1],
          filter: [BLUR_INACTIVE, "blur(0px)"],
          duration: CONTENT_REVEAL_MS,
          ease: "easeOutCubic",
        },
        1150,
      );

      tlRef.current = tl;
      startDrawLoop();
    } else {
      // ── Videos 2–4: all panels visible → rotation → incoming unblurs ───
      // startAt: clear. All others: blurred.
      // During rotation: startAt blurs in, panelKey unblurs out.
      // Timeline: 200ms beat + 1000ms rotation + 800ms unblur + 5000ms still = 7000ms
      const BEAT_MS = 200;
      const ROTATE_MS = 1000;
      const TOTAL_MS = BEAT_MS + ROTATE_MS + CONTENT_REVEAL_MS + STILL_FRAME_MS;
      totalFrames.current = Math.ceil((TOTAL_MS / 1000) * FPS);

      const startSteps = POSITION_INDEX[config.startAt];
      const startRots = rotationsAtStep(startSteps);
      const endRots = rotationsAtStep(startSteps + 1);

      // Instantly: all panels at opacity 1, correct rotation
      // startAt is clear, all others blurred
      COMPONENT_ORDER.forEach((key) => {
        const el = panelRefs.current[key];
        if (!el) return;
        const isFlipped = FLIPPED_PANELS.has(key);
        const isStartAt = key === config.startAt;
        animate(el, {
          opacity: isStartAt ? 1 : OPACITY_INACTIVE,
          filter: isStartAt ? "blur(0px)" : BLUR_INACTIVE,
          rotate: startRots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
      });

      const tl = createTimeline({ autoplay: false });

      // Rotate all panels 90° after the beat
      COMPONENT_ORDER.forEach((key) => {
        const el = panelRefs.current[key];
        if (!el) return;
        const isFlipped = FLIPPED_PANELS.has(key);
        tl.add(
          el,
          {
            rotate: endRots[key],
            ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
            duration: ROTATE_MS,
            ease: "easeOutExpo",
          },
          BEAT_MS,
        );
      });

      // Outgoing panel blurs in during rotation
      tl.add(
        `.cross-${config.startAt}`,
        {
          opacity: [1, OPACITY_INACTIVE],
          filter: ["blur(0px)", BLUR_INACTIVE],
          duration: ROTATE_MS,
          ease: "easeInQuad",
        },
        BEAT_MS,
      );

      // Incoming panel unblurs after rotation
      tl.add(
        `.cross-${config.panelKey}`,
        {
          opacity: [OPACITY_INACTIVE, 1],
          filter: [BLUR_INACTIVE, "blur(0px)"],
          duration: CONTENT_REVEAL_MS,
          ease: "easeOutCubic",
        },
        BEAT_MS + ROTATE_MS,
      );

      tlRef.current = tl;
      startDrawLoop();
    }

    return () => stopDrawLoop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Per-frame capture ──────────────────────────────────────────────────
  // The RAF loop increments frameCount (useState), which triggers a React
  // re-render. After each render this effect fires: it seeks the timeline to
  // the correct time so Mechanic captures the fully-rendered DOM state.
  useEffect(() => {
    if (!tlRef.current || totalFrames.current === 0) return;

    const elapsedMs = (frameCount / FPS) * 1000;
    tlRef.current.seek(elapsedMs);

    if (frameCount < totalFrames.current) {
      mechanic.frame();
    } else {
      stopDrawLoop();
      mechanic.done();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameCount]);

  // ── Per-panel body content mapped from inputs ──────────────────────────
  const panelContent = {
    aktuell: (
      <>
        <h1 className="cross-line">{artistName}</h1>
        <h1 className="cross-line" style={{ fontStyle: "italic" }}>
          {exhibitionTitle}
        </h1>
        <h1 className="cross-line">{dateSpan}</h1>
      </>
    ),
    ausblick: (
      <>
        <h1 className="cross-line">{vernissageDate}</h1>
        <h1 className="cross-line">{vernissageAddress}</h1>
        <h1 className="cross-line">{vernissageTime}</h1>
      </>
    ),
    info: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {exhibitionTheme}
        </p>
      </>
    ),
    archiv: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {artistDetails}
        </p>
      </>
    ),
  };

  // ── Render ─────────────────────────────────────────────────────────────
  // Note: we never apply the `inactive` CSS class here — anime.js controls
  // all opacity/filter values via inline styles. The inactive class is for
  // interactive mode only and would fight the timeline seeks.
  return (
    <div
      className="cross-root"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor,
        color: textColor,
      }}
    >
      {COMPONENT_ORDER.map((key) => (
        <div key={key} className="cross-container">
          <div ref={setPanelRef(key)} className={`cross-panel cross-${key}`}>
            <h3 className="cross-stepname">{panelLabels[key]}</h3>
            <div className="cross-body">{panelContent[key]}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Inputs ───────────────────────────────────────────────────────────────────

export const inputs = {
  // ── Canvas ──────────────────────────────────────────────────────────────
  width: {
    type: "number",
    default: 1080,
    label: "Width (px)",
  },
  height: {
    type: "number",
    default: 1080,
    label: "Height (px)",
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#ffffff",
    label: "Background",
  },
  textColor: {
    type: "color",
    model: "hex",
    default: "#0a1ef5",
    label: "Text Color",
  },
  inactiveBlur: {
    type: "number",
    default: 9,
    min: 0,
    max: 40,
    slider: true,
    label: "Inactive Blur (px)",
  },
  inactiveOpacity: {
    type: "number",
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
    slider: true,
    label: "Inactive Opacity",
  },

  // ── Video step selector ────────────────────────────────────────────────
  videoStep: {
    type: "text",
    default: VIDEO_STEPS[0],
    options: VIDEO_STEPS,
    label: "Video Step",
  },

  // ── Step name labels ──────────────────────────────────────────────────
  label1: {
    type: "text",
    default: "Neue Ausstellung",
    label: "Label – 1",
  },
  label2: {
    type: "text",
    default: "Vernissage",
    label: "Label – 2",
  },
  label3: {
    type: "text",
    default: "Zum Thema",
    label: "Label – 3",
  },
  label4: {
    type: "text",
    default: "\u00dcber die K\u00fcnstler:in",
    label: "Label – 4",
  },

  // ── Video 1 – Aktuell ─────────────────────────────────────────────────
  exhibitionTitle: {
    type: "text",
    default: "«Orte der Stille»",
    label: "Exhibition Title",
  },
  artistName: {
    type: "text",
    default: "Alexandre Joly",
    label: "Artist Name",
  },
  dateSpan: {
    type: "text",
    default: "01.03. – 02.06.26",
    label: "Date Span",
  },

  // ── Video 2 – Ausblick (Vernissage) ───────────────────────────────────
  vernissageDate: {
    type: "text",
    default: "Freitag, 28. Februar 2026",
    label: "Vernissage Date",
  },

  vernissageAddress: {
    type: "text",
    default: "Musterstrasse 12, 8000 Zürich",
    label: "Vernissage Address",
  },
  vernissageTime: {
    type: "text",
    default: "18:00 Uhr",
    label: "Vernissage Time",
  },

  // ── Video 3 – Info (Theme) ────────────────────────────────────────────
  exhibitionTheme: {
    type: "text",
    default:
      "Die Ausstellung erkundet das Spannungsfeld zwischen urbanem Raum und Natur.",
    label: "Exhibition Theme / Description",
  },

  // ── Video 4 – Archiv (Artist) ─────────────────────────────────────────
  artistDetails: {
    type: "text",
    default:
      "Vorname Nachname (*1990) lebt und arbeitet in Zürich. Studium an der ZHdK.",
    label: "Artist Details / Bio",
  },
};

// ─── Settings ─────────────────────────────────────────────────────────────────

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Cross Rotation",
  animated: true,
  hideScaleToFit: true,
  initialScaleToFit: false,
  hideAutoRefresh: true,
  hideFunction: true,
};
